import { GridHookForm, FormItem } from 'vnetwork-ui'
import { classifyCustomerLeadType } from '@/modules/Customer/services'
import { CustomerLeadTypes } from '@/modules/Marketing/types/utils'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Dialog } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

// Inline legend component with highlight support
interface LegendCardProps {
    title: string
    borderColor: string
    items: { label: string; highlight?: boolean; strikethrough?: boolean }[]
    example?: string
    isActive?: boolean
}

const LegendCard: React.FC<LegendCardProps> = ({
    title,
    borderColor,
    items,
    example,
    isActive,
}) => {
    const { t } = useTranslation('marketing')

    return (
        <div
            className={`rounded p-3 text-xs transition-all ${
                isActive
                    ? 'bg-primary/10 ring-2 ring-primary shadow-md'
                    : 'bg-white opacity-60'
            }`}
            style={{ borderLeft: `3px solid ${borderColor}` }}
        >
            <div className="font-semibold text-gray-800 mb-2">{title}</div>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
                {items.map((item, idx) => (
                    <li key={idx}>
                        {item.strikethrough ? (
                            <span className="text-red-500 line-through italic">
                                {item.label}
                            </span>
                        ) : item.highlight ? (
                            <span className="text-red-500">{item.label}</span>
                        ) : (
                            item.label
                        )}
                    </li>
                ))}
            </ul>
            {example && (
                <div className="mt-2 text-gray-500 italic text-[10px]">
                    {t('lead_flow.example')}: {example}
                </div>
            )}
        </div>
    )
}

const LeadTypeLegend: React.FC<{ selectedType?: string }> = ({
    selectedType,
}) => {
    const { t } = useTranslation('marketing')

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
            <LegendCard
                title={t('lead_flow.legend.warm_lead')}
                borderColor="#F59E0B"
                items={[
                    { label: t('lead_flow.legend.authority_yes') },
                    {
                        label: t('lead_flow.legend.need_interested'),
                        highlight: true,
                    },
                ]}
                example={t('lead_flow.legend.warm_lead_example')}
                isActive={selectedType === 'warm-lead'}
            />
            <LegendCard
                title={t('lead_flow.legend.cool_lead')}
                borderColor="#D1D5DB"
                items={[
                    { label: t('lead_flow.legend.budget_yes') },
                    { label: t('lead_flow.legend.authority_yes') },
                    {
                        label: t('lead_flow.legend.need_not_yet'),
                        highlight: true,
                    },
                ]}
                example={t('lead_flow.legend.cool_lead_example')}
                isActive={selectedType === 'cool-lead'}
            />
            <LegendCard
                title={t('lead_flow.legend.sql')}
                borderColor="#22C55E"
                items={[
                    { label: t('lead_flow.legend.budget_yes') },
                    { label: t('lead_flow.legend.authority_yes') },
                    {
                        label: t('lead_flow.legend.need_has'),
                        highlight: true,
                    },
                    { label: t('lead_flow.legend.timeline_has') },
                ]}
                example={t('lead_flow.legend.sql_example')}
                isActive={selectedType === 'sale-qualified-lead'}
            />
            <LegendCard
                title={t('lead_flow.legend.unqualified')}
                borderColor="#F87171"
                items={[
                    {
                        label: t('lead_flow.legend.budget_no'),
                        strikethrough: true,
                    },
                    {
                        label: t('lead_flow.legend.authority_no'),
                        strikethrough: true,
                    },
                    {
                        label: t('lead_flow.legend.need_no'),
                        strikethrough: true,
                    },
                ]}
                example={t('lead_flow.legend.unqualified_example')}
                isActive={selectedType === 'un-qualified-lead'}
            />
        </div>
    )
}

export interface ClassifyLeadTypeProps extends ModalProps {
    customer_id: number
    current_lead_type?: string
}

interface ClassifyLeadTypeFormValues {
    lead_type: string
}

const ClassifyLeadType: React.FC<ClassifyLeadTypeProps> = ({
    onClose,
    open,
    customer_id,
    current_lead_type,
}) => {
    const { t } = useTranslation()

    const formControl = useForm<ClassifyLeadTypeFormValues>({
        defaultValues: {
            lead_type: current_lead_type || '',
        },
        resolver: yupResolver(
            yup.object().shape({
                lead_type: yup.string().required(t('required')),
            })
        ),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: classifyCustomerLeadType,
        },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t('success-classify-lead'),
            onSuccess: () => {
                onClose()
            },
        }
    )

    const handleClassify = (values: ClassifyLeadTypeFormValues) => {
        mutateAsync({
            customer_id,
            lead_type: values.lead_type,
        })
    }

    return (
        <Dialog
            title={t('customer:classify_lead_type')}
            onClose={onClose}
            open={open}
        >
            <Alert variant="info">
                {t('customer:classify_lead_description')}
            </Alert>
            <LeadTypeLegend selectedType={formControl.watch('lead_type')} />
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('save'),
                        type: 'submit',
                        variant: 'contained',
                        disabled: isPending,
                        loading: isPending,
                    },
                ]}
                data={[
                    {
                        className: 'md:col-span-4 col-span-1',
                        renderEditComponent: (control) => (
                            <FormItem
                                control={control}
                                name="lead_type"
                                type="select"
                                label={t('customer:lead_type.label')}
                                placeholder={t(
                                    'customer:lead_type.placeholder'
                                )}
                                options={CustomerLeadTypes(t)}
                            />
                        ),
                    },
                ]}
                onSubmit={handleClassify}
                className="mt-4"
            />
        </Dialog>
    )
}

export default ClassifyLeadType
